
import {Component, Mixins, Emit, Watch} from 'vue-property-decorator';
import {MDB} from '@/plugins/uyap-plugin/MDB';
import {IbanTercihiEnum} from '@/plugins/uyap-plugin/uyap/IbanTercihiEnum';
import {MtsDosyaFormData} from '@/plugins/uyap-plugin/takip_ac/MtsDosyaFormData';
import TakipHazirlaOptions from '@/components/forms/form-wizard/eicrapro/takip-hazirla/TakipHazirlaOptions.vue';
import XmlUploadInput from '@/components/forms/form-wizard/eicrapro/shared/XmlUploadInput.vue';
import ObjectInputMixin from '@/mixins/ObjectInputMixin.vue';
import TelefonNumarasi from '@/components/inputs/TelefonNumarasi.vue';
import EPosta from '@/components/inputs/EPosta.vue';
import {AvukatYetkiliKurumlarItem} from '@/plugins/uyap-plugin/uyap/AvukatYetkiliKurumlar';
import FormDialog from '@/components/dialogs/FormDialog.vue';
import BankaHesabiForm from '@/components/forms/BankaHesabiForm.vue';
import {BankaHesabiEntity} from '@/entity/BankaHesabiEntity';
import {PersonelEntity} from '@/entity/PersonelEntity';
import TamamlanmayanTakipList from "@/components/eicrapro/TamamlanmayanTakipList.vue";

type XmlKaynak = 'dosya' | 'liste';

interface MtsKurumIletisimSelectItem {
  id: number;
  kisi_kurum_id: string;
  kisiKurumId: string;
  tur: string;
  value: string;
  created_at: string;
  updated_at: string;
}

@Component({
  components: {
    EPosta,
    TelefonNumarasi,
    FormDialog,
    BankaHesabiForm,
    TakipHazirlaOptions,
    XmlUploadInput,
    TamamlanmayanTakipList,
  },
})
export default class MtsTakipBilgileriForm extends Mixins(ObjectInputMixin) {
  // ObjectInputMixin'den gelen tipler
  localValue!: MtsDosyaFormData & { xml?: File | null };
  input!: (value?: any, isChangeData?: boolean) => void;

  // Refs
  $refs!: {
    form: any;
    yeniTelefonInput?: TelefonNumarasi;
    yeniEpostaInput?: EPosta;
  };

  // Local state
  mdb = new MDB();
  xmlValidationError: string | null = null;
  xmlKaynak: XmlKaynak = 'dosya';

  telListeYukleniyor = false;
  mailListeYukleniyor = false;
  telEklemeYukleniyor = false;
  mailEklemeYukleniyor = false;
  telEklemePanelAcik = false;
  mailEklemePanelAcik = false;
  yeniTelefon = '';
  yeniEposta = '';
  telSelectItems: MtsKurumIletisimSelectItem[] = [];
  mailSelectItems: MtsKurumIletisimSelectItem[] = [];

  /** Backend'de Vakıfbank için kullanılan banka_id (gerekirse güncellenir). */
  private readonly VAKIFBANK_BANKA_ID = 1;

  vakifbankHesaplari: BankaHesabiEntity[] = [];
  ibanListeYukleniyor = false;

  // Enums
  IbanTercihiEnum = IbanTercihiEnum;

  // Options
  ibanTercihiItems = [
    {text: 'Avukat', value: IbanTercihiEnum.Avukat},
    {text: 'Müvekkil', value: IbanTercihiEnum.Muvekkil}
  ];

  fileRule = (file: File | null) => {
    if (!file) return true;
    const isXml = file.name.toLowerCase().endsWith('.xml');
    return isXml || 'Sadece XML dosyası yükleyebilirsiniz';
  };

  // Computed properties
  get showXmlUploadZone(): boolean {
    return this.xmlKaynak === 'dosya'
        || (this.xmlKaynak === 'liste' && !!this.localValue.xml);
  }

  secXmlKaynak(kaynak: 'dosya' | 'liste') {
    if (this.xmlKaynak === kaynak) {
      return;
    }
    this.xmlKaynak = kaynak;
    this.$set(this.localValue, 'xml', null);
    this.xmlValidationError = null;
    this.input();
  }

  // Vuex getters
  get adliyeler(): any[] {
    return this.$store.getters['mtsTakipHazirla/adliyeler'] || [];
  }

  get yetkiliKurumlar(): Array<AvukatYetkiliKurumlarItem> {
    return this.$store.getters['mtsTakipHazirla/yetkiliKurumlar'] || [];
  }

  get mtsKurumSeciliMi(): boolean{
    return this.localValue.alacakliTaraf && this.localValue.alacakliTaraf.kisiKurumId;
  }

  get personelId(): number | null {
    const u = this.$store.state.user as PersonelEntity;
    return typeof u.id === 'number' ? u.id : null;
  }

  get personelBankaHesabiKayitAction(): string {
    if (!this.personelId) {
      return '';
    }
    return `/api/v1/personel/${this.personelId}/banka-hesabi`;
  }

  get bankaHesabiFormDialogDefaultValue(): { banka_id: number } {
    return {banka_id: this.VAKIFBANK_BANKA_ID};
  }

  get vakifbankIbanSelectItems(): Array<BankaHesabiEntity & { secimEtiketi: string }> {
    return this.vakifbankHesaplari.map((h) => ({
      ...h,
      secimEtiketi: h.isim ? `${h.isim} — ${h.iban}` : h.iban,
    }));
  }

  // Lifecycle
  async mounted() {
    await this.$store.dispatch('mtsTakipHazirla/fetchYetkiliKurumlar');

    if (this.localValue.ibanTercihi === IbanTercihiEnum.Avukat) {
      await this.yukleVakifbankIbanlari();
    }
  }

  // Watchers - Store entegrasyonu
  @Watch('localValue.il')
  async onIlChange(newIl: string, oldIl: string) {
    if (newIl !== oldIl) {
      // Adliye seçimini temizle
      this.$set(this.localValue, 'adliye', null);
      // Yeni il için adliyeleri yükle
      if (newIl) {
        await this.$store.dispatch('mtsTakipHazirla/fetchAdliyeler', newIl);
      }
      this.input();
    }
  }

  @Watch('localValue.ibanTercihi')
  async onIbanTercihiChange(newTercih: IbanTercihiEnum, oldTercih: IbanTercihiEnum) {
    if (newTercih !== oldTercih) {
      if (newTercih !== IbanTercihiEnum.Avukat) {
        this.localValue.avukatIban = '';
        this.input();
      } else {
        await this.yukleVakifbankIbanlari();
      }
    }
  }

  uygulaVarsayilanAvukatIban(tercihEdilenIban?: string): void {
    if (this.vakifbankHesaplari.length === 0) {
      return;
    }
    if (tercihEdilenIban && this.vakifbankHesaplari.some((h) => h.iban === tercihEdilenIban)) {
      this.localValue.avukatIban = tercihEdilenIban;
      return;
    }
    const mevcut = this.localValue.avukatIban;
    const listede = this.vakifbankHesaplari.some((h) => h.iban === mevcut);
    if (!listede) {
      this.localValue.avukatIban = this.vakifbankHesaplari[0].iban;
    }
  }

  async yukleVakifbankIbanlari(tercihEdilenIban?: string): Promise<void> {
    if (!this.personelId) {
      this.vakifbankHesaplari = [];
      this.$toast.error('Oturum bilgisi bulunamadı; banka hesapları yüklenemiyor.');
      this.input();
      return;
    }

    this.ibanListeYukleniyor = true;
    try {
      const res = await this.$http.get(`/api/v1/personel/${this.personelId}/bankalar`, {
        params: {banka_id: this.VAKIFBANK_BANKA_ID},
      });
      this.vakifbankHesaplari = res.data.banka_hesaplari || [];
      this.uygulaVarsayilanAvukatIban(tercihEdilenIban);
    } catch (e) {
      console.error('Vakıfbank hesapları yüklenemedi', e);
      this.$toast.error('Banka hesapları alınamadı.');
      this.vakifbankHesaplari = [];
    } finally {
      this.ibanListeYukleniyor = false;
    }
    this.input();
  }

  async onVakifbankHesabiEklendi(kayit: BankaHesabiEntity): Promise<void> {
    const iban = kayit && kayit.iban;
    await this.yukleVakifbankIbanlari(iban);
    this.$toast.success('Banka hesabı kaydedildi.');
  }

  async alacakliTarafIletisiminiUygula(): Promise<void> {
    this.yeniTelefon = '';
    this.yeniEposta = '';
    this.telEklemePanelAcik = false;
    this.mailEklemePanelAcik = false;
    this.telSelectItems = [];
    this.mailSelectItems = [];
    this.localValue.alacakliTelefon = '';
    this.localValue.alacakliEposta = '';
    this.input();
    await this.yukleKurumIletisimListeleri();
  }

  telEklemePaneliniAcKapat(): void {
    this.telEklemePanelAcik = !this.telEklemePanelAcik;
    if (!this.telEklemePanelAcik) {
      this.yeniTelefon = '';
    }
  }

  mailEklemePaneliniAcKapat(): void {
    this.mailEklemePanelAcik = !this.mailEklemePanelAcik;
    if (!this.mailEklemePanelAcik) {
      this.yeniEposta = '';
    }
  }

  telefonEkleIptal(): void {
    this.yeniTelefon = '';
    this.telEklemePanelAcik = false;
  }

  postaEkleIptal(): void {
    this.yeniEposta = '';
    this.mailEklemePanelAcik = false;
  }

  async getMtsKurumIletisimListesi(tur: 'tel' | 'mail') {
    const kisiKurumId = this.localValue.alacakliTaraf?.kisiKurumId;
    if (kisiKurumId) {
      try {
        if (tur === 'tel')
          this.telListeYukleniyor = true;
        else
          this.mailListeYukleniyor = true;

        const res = await this.$http.get('/api/v1/mts-kurum-iletisim-bilgisi', {
          params: {kisiKurumId, tur},
        })

        if (tur == "tel")
          this.telSelectItems = res.data;
        else
          this.mailSelectItems = res.data;
      } catch (e) {
        console.error("Bilgiler alınamadı.", e);
        this.$toast.error("İletişim bilgileri alınamadı.");
      } finally {
        if (tur === 'tel')
          this.telListeYukleniyor = false;
        else
          this.mailListeYukleniyor = false;
      }
    }
  }

  async yukleKurumIletisimListeleri(): Promise<void> {
    try {
      await Promise.all([
        this.getMtsKurumIletisimListesi('tel'),
        this.getMtsKurumIletisimListesi('mail'),
      ]);
      this.localValue.alacakliTelefon = this.telSelectItems[0]?.value??'';
      this.localValue.alacakliEposta = this.mailSelectItems[0]?.value??'';
    } catch (e) {
      console.error('MTS kurum iletişim listesi yüklenemedi', e);
      this.$toast.error('İletişim bilgileri yüklenemedi.');
      this.telSelectItems = [];
      this.mailSelectItems = [];
      this.localValue.alacakliTelefon = '';
      this.localValue.alacakliEposta = '';
    } finally {
      this.input();
    }
  }

  async telefonEkle(): Promise<void> {
    const id = this.localValue.alacakliTaraf.kisiKurumId;
    if (!id) {
      return;
    }
    const telRef = this.$refs.yeniTelefonInput;
    if (!telRef || !telRef.validateField()) {
      return;
    }
    const value = this.yeniTelefon;
    this.telEklemeYukleniyor = true;
    try {
      await this.$http.post(
          '/api/v1/mts-kurum-iletisim-bilgisi',
          {kisiKurumId: id.toString(), tur: 'tel', value},
          {headers: {'Content-Type': 'application/json'}},
      );
      this.yeniTelefon = '';
      this.telEklemePanelAcik = false;
      await this.getMtsKurumIletisimListesi('tel');
      this.localValue.alacakliTelefon = value;
      this.$toast.success('Telefon kaydedildi.');
      this.input();
    } catch (e) {
      console.error('MTS telefon eklenemedi', e);
      this.$toast.error('Telefon kaydedilemedi.');
    } finally {
      this.telEklemeYukleniyor = false;
    }
  }

  async postaEkle(): Promise<void> {
    const postaRef = this.$refs.yeniEpostaInput;
    if (!postaRef || !postaRef.validateField()) {
      return;
    }
    const value = this.yeniEposta.trim();
    this.mailEklemeYukleniyor = true;
    try {
      await this.$http.post('/api/v1/mts-kurum-iletisim-bilgisi', {
        kisiKurumId: this.localValue.alacakliTaraf.kisiKurumId.toString(),
        tur: 'mail', value
      });
      this.yeniEposta = '';
      this.mailEklemePanelAcik = false;
      await this.getMtsKurumIletisimListesi('mail');
      this.localValue.alacakliEposta = value;
      this.$toast.success('E-posta kaydedildi.');
      this.input();
    } catch (e) {
      console.error('MTS e-posta eklenemedi', e);
      this.$toast.error('E-posta kaydedilemedi.');
    } finally {
      this.mailEklemeYukleniyor = false;
    }
  }

  async removeIletisimKaydi(item: MtsKurumIletisimSelectItem): Promise<void> {
    try {
      await this.$http.delete(`/api/v1/mts-kurum-iletisim-bilgisi/${item.id}`);
      if (item.tur === 'tel') {
        const secilenSilindi = this.localValue.alacakliTelefon == item.value;
        await this.getMtsKurumIletisimListesi('tel');
        if (secilenSilindi)
          this.localValue.alacakliTelefon = this.telSelectItems[0] ? this.telSelectItems[0].value : '';
      } else {
        const secilenSilindi = this.localValue.alacakliEposta == item.value;
        await this.getMtsKurumIletisimListesi('mail');
        if (secilenSilindi)
          this.localValue.alacakliEposta = this.mailSelectItems[0] ? this.mailSelectItems[0].value : '';
      }
      this.$toast.success('Kayıt silindi.');
      this.input();
    } catch (e) {
      console.error('MTS iletişim silinemedi', e);
      this.$toast.error('Kayıt silinemedi.');
    }
  }

  handleXmlFileSelected(file: File | null) {
    // Local value'yu güncelle (reactive - ObjectInputMixin sayesinde)
    this.$set(this.localValue, 'xml', file);

    // Parent'ı güncelle
    this.input();
    this.$emit('xml-selected', file);
  }

  @Emit('validation-error')
  handleXmlValidationError(message: string | null) {
    if (typeof message === 'string' && message.length) {
      this.xmlValidationError = message;
    } else {
      this.xmlValidationError = null;
    }
    return message;
  }

  // Public method for parent to validate form
  public async validate(): Promise<boolean> {
    this.telEklemePanelAcik = false;
    this.mailEklemePanelAcik = false;
    await this.$nextTick;
    if (!this.$refs.form) {
      return false;
    }
    return this.$refs.form.validate();
  }
}
